import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';

interface Prediction {
  id: string;
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
  points?: number;
  exactScorePoints?: number;
  correctResultPoints?: number;
  isScored: boolean;
  match: {
    id: string;
    matchDate: string;
    homeGoals?: number;
    awayGoals?: number;
    status: string;
    homeTeam: {
      name: string;
      shortName?: string;
    };
    awayTeam: {
      name: string;
      shortName?: string;
    };
  };
  season: {
    id: string;
    name: string;
  };
  matchGroup?: {
    id: string;
    name: string;
    matchWeek?: number | null;
  };
}

interface Season {
  id: string;
  name: string;
  order?: number;
  startDate?: string;
}

interface TimelineDataPoint {
  date: string; // Group key (match group id)
  label: string; // Display label (e.g. Matchweek 3)
  matchWeek: number;
  predictions: Prediction[];
  count: number; // Total count for bar height
}

const getMatchweekNumber = (pred: Prediction): number => {
  if (pred.matchGroup?.matchWeek != null) {
    return pred.matchGroup.matchWeek;
  }
  const fromName = pred.matchGroup?.name?.match(/(\d+)/);
  return fromName ? parseInt(fromName[1], 10) : 999;
};

const getMatchweekLabel = (pred: Prediction, includeSeason: boolean): string => {
  const week = getMatchweekNumber(pred);
  const weekLabel = week < 999 ? `Round ${week}` : (pred.matchGroup?.name || '-');
  if (includeSeason) {
    return `${pred.season.name} — ${weekLabel}`;
  }
  return weekLabel;
};

type TimeSpan = '1month' | '3months' | 'all';
type PredictionStatus = 'exact' | 'correct' | 'incorrect' | 'unscored';

const PredictionTimeline: React.FC = () => {
  const { user } = useAuth();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [timeSpan, setTimeSpan] = useState<TimeSpan>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLegendTooltip, setShowLegendTooltip] = useState(false);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLegendTooltip && !target.closest('.legend-tooltip-container')) {
        setShowLegendTooltip(false);
      }
    };

    if (showLegendTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLegendTooltip]);

  // Fetch seasons for filtering
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await apiClient.get('/seasons?active=true');
        const fetchedSeasons = response.data;
        setSeasons(fetchedSeasons);
      } catch (err) {
        console.error('Error fetching seasons:', err);
      }
    };

    fetchSeasons();
  }, []);

  // Fetch ALL predictions (not filtered by season) for stats calculation
  // The season filtering happens in filteredPredictions for display only
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchPredictions = async () => {
      // Check cache - always fetch ALL predictions, not filtered by season
      const cacheKey = `prediction_timeline_all_${user.id}`;
      const cacheTimestampKey = `prediction_timeline_timestamp_all_${user.id}`;
      
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
      
      if (cachedData && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const fifteenMinutes = 15 * 60 * 1000;
          
          if (now - timestamp < fifteenMinutes) {
            const parsed = JSON.parse(cachedData);
            setPredictions(parsed);
            setLoading(false);
            return;
          } else {
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(cacheTimestampKey);
          }
        } catch (e) {
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        }
      }

      try {
        setLoading(true);
        // Always fetch ALL predictions (no season filter) for accurate stats
        const response = await apiClient.get('/predictions');
        
        // Sort by match date
        const sorted = (response.data || []).sort((a: Prediction, b: Prediction) => 
          new Date(a.match.matchDate).getTime() - new Date(b.match.matchDate).getTime()
        );
        
        setPredictions(sorted);
        
        // Cache the data
        sessionStorage.setItem(cacheKey, JSON.stringify(sorted));
        sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
      } catch (err) {
        console.error('Error fetching predictions:', err);
        setError('Failed to load predictions');
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, [user]); // Remove selectedSeason dependency - always fetch ALL predictions

  // Total points per season for tab labels
  const seasonStats = useMemo(() => {
    const stats: Record<string, { points: number }> = {};

    predictions.forEach(pred => {
      const seasonId = pred.season.id;
      if (!stats[seasonId]) {
        stats[seasonId] = { points: 0 };
      }
      stats[seasonId].points += pred.points || 0;
    });

    return stats;
  }, [predictions]);

  // Calculate which seasons have any predictions (scored or unscored) for showing tabs
  const seasonsWithPredictions = useMemo(() => {
    const seasonIds = new Set<string>();
    predictions.forEach(pred => {
      seasonIds.add(pred.season.id);
    });
    return seasonIds;
  }, [predictions]);

  const getSeasonLatestActivity = (seasonId: string): number => {
    const seasonPredictions = predictions.filter(pred => pred.season.id === seasonId);
    if (seasonPredictions.length === 0) return 0;
    return Math.max(
      ...seasonPredictions.map(pred => new Date(pred.match.matchDate).getTime())
    );
  };

  // Active competitions first, most recently active on the left
  const sortedVisibleSeasons = useMemo(() => {
    return seasons
      .filter(season => seasonsWithPredictions.has(season.id))
      .sort((a, b) => {
        const activityDiff = getSeasonLatestActivity(b.id) - getSeasonLatestActivity(a.id);
        if (activityDiff !== 0) return activityDiff;
        return (a.order || 0) - (b.order || 0);
      });
  }, [seasons, seasonsWithPredictions, predictions]);

  // Default to the most recently active competition on initial load
  useEffect(() => {
    if (selectedSeason || sortedVisibleSeasons.length === 0) return;
    setSelectedSeason(sortedVisibleSeasons[0].id);
  }, [selectedSeason, sortedVisibleSeasons]);

  // Keep selection valid when tabs change
  useEffect(() => {
    if (sortedVisibleSeasons.length === 0) return;

    const isValidSelection = sortedVisibleSeasons.some(season => season.id === selectedSeason);

    if (!isValidSelection) {
      setSelectedSeason(sortedVisibleSeasons[0].id);
    }
  }, [sortedVisibleSeasons, selectedSeason]);

  const effectiveSeasonId = selectedSeason || sortedVisibleSeasons[0]?.id || '';

  // Filter predictions by selected season, time span, and include up to 1 week in the future
  const filteredPredictions = useMemo(() => {
    if (!predictions.length) return [];

    if (!effectiveSeasonId) return [];

    const seasonFiltered = predictions.filter(pred => pred.season.id === effectiveSeasonId);

    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today
    
    // Allow predictions up to 1 week in the future
    const futureCutoff = new Date(now);
    futureCutoff.setDate(futureCutoff.getDate() + 5); // 7 days from today
    futureCutoff.setHours(23, 59, 59, 999); // End of that day
    
    const cutoffDate = new Date();

    // Filter predictions: show past predictions and up to 1 week in the future
    const visiblePredictions = seasonFiltered.filter(pred => {
      const matchDate = new Date(pred.match.matchDate);
      return matchDate <= futureCutoff;
    });

    // Finally, filter by time span
    switch (timeSpan) {
      case '1month':
        cutoffDate.setMonth(cutoffDate.getMonth() - 1);
        break;
      case '3months':
        cutoffDate.setMonth(cutoffDate.getMonth() - 3);
        break;
      case 'all':
        // No cutoff, return all visible predictions (past + up to 1 week future) for selected season
        return visiblePredictions;
    }

    return visiblePredictions.filter(pred => {
      const matchDate = new Date(pred.match.matchDate);
      return matchDate >= cutoffDate;
    });
  }, [predictions, effectiveSeasonId, timeSpan]);

  // Group predictions by matchweek
  const timelineData = useMemo((): TimelineDataPoint[] => {
    if (!filteredPredictions.length) return [];

    const grouped = new Map<string, Prediction[]>();
    filteredPredictions.forEach(pred => {
      const key = pred.matchGroup?.id || `fallback-${pred.match.matchDate.split('T')[0]}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(pred);
    });

    return Array.from(grouped.entries())
      .map(([date, predictions]) => {
        const sortedPredictions = predictions.sort((a, b) =>
          new Date(a.match.matchDate).getTime() - new Date(b.match.matchDate).getTime()
        );
        const first = sortedPredictions[0];

        return {
          date,
          label: getMatchweekLabel(first, false),
          matchWeek: getMatchweekNumber(first),
          predictions: sortedPredictions,
          count: sortedPredictions.length,
        };
      })
      .sort((a, b) => {
        if (a.matchWeek !== b.matchWeek) {
          return a.matchWeek - b.matchWeek;
        }
        return a.date.localeCompare(b.date);
      });
  }, [filteredPredictions]);

  // Calculate correct predictions count from filtered predictions
  const filteredCorrectCount = useMemo(() => {
    return filteredPredictions.filter(pred => {
      const isCorrect = pred.isScored && (
        (pred.exactScorePoints && pred.exactScorePoints > 0) ||
        (pred.correctResultPoints && pred.correctResultPoints > 0)
      );
      return isCorrect;
    }).length;
  }, [filteredPredictions]);

  // Helper function to determine prediction status
  const getPredictionStatus = (pred: Prediction): PredictionStatus => {
    if (!pred.isScored) return 'unscored';
    if (pred.exactScorePoints && pred.exactScorePoints > 0) return 'exact';
    if (pred.correctResultPoints && pred.correctResultPoints > 0) return 'correct';
    return 'incorrect';
  };

  // Get color for prediction status (hex colors for recharts)
  const getStatusColorHex = (status: PredictionStatus): string => {
    switch (status) {
      case 'exact':
        return '#e3bb09'; // Yellow-500
      case 'correct':
        return '#10b981'; // Green-500
      case 'incorrect':
        return '#ef4444'; // Red-500
      case 'unscored':
        return 'rgba(107, 114, 128, 0.4)'; // Gray-400 with 50% opacity for light/dark mode compatibility
      default:
        return '#6b7280';
    }
  };

  // Custom shape for rendering stacked boxes
  // Recharts uses plot coordinates where (0,0) is top-left of the plot area
  // The props.x and props.y are in plot coordinates, not absolute SVG coordinates
  // For bars growing upward, y represents the top of the bar
  // The baseline (x-axis) is at the bottom of the plot area
  const CustomBar = (props: any) => {
    const { payload, x, y, width, height } = props;
    if (!payload || !payload.predictions || payload.predictions.length === 0) {
      return null;
    }

    const boxes: React.ReactElement[] = [];
    
    // In recharts, custom shapes receive coordinates in the plot area coordinate system
    // (0,0) is top-left of the plot area, y increases downward
    // For vertical bars, props.y is the TOP of where recharts calculated the bar should be
    // For bars growing upward from baseline, props.y would be near the baseline
    // The baseline (x-axis) is at the bottom of the plot area
    
    // Chart plot area dimensions (excluding margins)
    // Chart height: 300px, margins: top=5, bottom varies by chart type (60-75), left=12, right=12
    // Always use angled labels, so always need bottom margin for angled text
    const bottomMargin = 50;
    const topMargin = 5;
    const chartHeight = 300;
    const plotAreaHeight = chartHeight - topMargin - bottomMargin; // Height of plot area in pixels (e.g., 300 - 5 - 60 = 235px)
    
    // Calculate box size and gap to fit within available height
    // Scale based on the tallest stack across ALL columns to ensure all stacks fit
    const maxPredictions = Math.max(...timelineData.map(d => d.count), 1);
    const gap = 2;
    
    // Calculate optimal box size to fit the tallest stack within plot area
    // Leave small padding at top (2px) to prevent clipping
    const topPadding = 2;
    const availableHeight = plotAreaHeight - topPadding; // Available height for boxes
    
    // Set min and max box sizes to prevent overlap and ensure visibility
    // Use smaller max size on mobile to prevent overlap with narrower columns
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const minBoxSize = 8;
    // On mobile, use smaller max size and also consider column width
    let maxBoxSize = isMobile ? 10 : 16; // Smaller on mobile (10px) to prevent overlap
    
    // On mobile, also consider the actual column width to prevent overflow
    // Columns can be very narrow when showing many dates, so limit box size accordingly
    if (isMobile && width && width > 0) {
      // Ensure box doesn't exceed 70% of column width to leave margin and prevent overlap
      const maxWidthBasedOnColumn = Math.floor(width * 0.7);
      maxBoxSize = Math.min(maxBoxSize, Math.max(minBoxSize, maxWidthBasedOnColumn));
    }
    
    let boxSize: number;
    if (maxPredictions === 0) {
      boxSize = 12; // Default size when no predictions
    } else {
      // Calculate box size so tallest stack fits exactly:
      // Formula: availableHeight = (boxSize * maxPredictions) + (gap * (maxPredictions - 1))
      // Solving for boxSize: boxSize = (availableHeight - gap * (maxPredictions - 1)) / maxPredictions
      const calculatedSize = (availableHeight - (gap * (maxPredictions - 1))) / maxPredictions;
      // Ensure boxes are within min/max bounds to prevent overlap and maintain visibility
      boxSize = Math.max(minBoxSize, Math.min(maxBoxSize, calculatedSize));
      
      // Double-check: verify the tallest stack will fit
      const totalStackHeight = (boxSize * maxPredictions) + (gap * (maxPredictions - 1));
      if (totalStackHeight > availableHeight) {
        // If it doesn't fit (due to rounding), scale down slightly
        const resized = (availableHeight - (gap * (maxPredictions - 1))) / maxPredictions;
        boxSize = Math.max(minBoxSize, Math.min(maxBoxSize, resized));
      }
    }
    
    // In Recharts, for vertical bars growing upward, props.y gives us the Y coordinate
    // where recharts wants to position the top of the bar. For a bar at the baseline,
    // props.y would be at the bottom of the plot area (baseline).
    // However, since we're using a custom shape and our data has count values,
    // props.y might not be exactly at the baseline.
    // 
    // The baseline (x-axis) is at the bottom of the plot area = plotAreaHeight
    // We want boxes to stack upward from the baseline.
    // In SVG, y is the top-left corner of the rect, so for a box at the baseline:
    // bottom_of_box = y + boxSize = plotAreaHeight
    // Therefore: y = plotAreaHeight - boxSize
    
    // The baseline (x-axis) is at the bottom of the plot area
    // In plot coordinates: y=0 is top of plot area, y=plotAreaHeight is bottom (baseline)
    const baseline = plotAreaHeight;
    
    // Start stacking from the baseline upward
    // Bottom of first box aligns with baseline
    // In SVG: y coordinate is top-left of rect, so bottom_of_box = y + boxSize
    // For baseline alignment: y + boxSize = baseline, therefore y = baseline - boxSize
    let currentY = baseline - boxSize;

    // Sort predictions to show earliest at bottom (chronological order)
    const sortedPredictions = [...payload.predictions].sort((a: Prediction, b: Prediction) => 
      new Date(a.match.matchDate).getTime() - new Date(b.match.matchDate).getTime()
    );

    sortedPredictions.forEach((pred: Prediction) => {
      const status = getPredictionStatus(pred);
      const color = getStatusColorHex(status);
      
      // Calculate centered x position
      const boxX = x + (width - boxSize) / 2;
      
      // Draw the box - it should already be within bounds due to our scaling
      boxes.push(
        <rect
          key={pred.id}
          x={boxX}
          y={currentY}
          width={boxSize}
          height={boxSize}
          fill={color}
          rx={3}
          stroke=""
          strokeWidth={0}
          className="hover:opacity-80 cursor-pointer"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
        />
      );
      
      // Move up for next box (decrease y since SVG y increases downward)
      currentY -= (boxSize + gap);
    });

    return <g>{boxes}</g>;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      if (data.predictions && data.predictions.length > 0) {
        return (
          <div 
            className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-lg shadow-xl p-3 max-w-xs pointer-events-none" 
            style={{ zIndex: 99999, transition: 'none', animation: 'none' }}
          >
            <p className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              {data.label}
            </p>
            <div className="space-y-1">
              {data.predictions.map((pred: Prediction) => {
                const status = getPredictionStatus(pred);
                return (
                  <div key={pred.id} className="text-xs">
                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {pred.match.homeTeam.shortName || pred.match.homeTeam.name} vs {pred.match.awayTeam.shortName || pred.match.awayTeam.name}
                    </span>
                    <br />
                    <span className="opacity-70" style={{ color: 'var(--text-secondary)' }}>
                      Actual: {pred.match.homeGoals !== null && pred.match.awayGoals !== null 
                        ? `${pred.match.homeGoals}-${pred.match.awayGoals}` 
                        : 'TBD'} | Predicted: {pred.homeGoals}-{pred.awayGoals}
                      {pred.isBanker ? ' ⭐' : ''}
                    </span>
                    <br />
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {status === 'exact' ? 'Exact Score ✨' :
                       status === 'correct' ? 'Correct Result ✓' :
                       status === 'incorrect' ? 'Incorrect ✗' :
                       'Not Yet Scored'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
    }
    return null;
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="mb-6">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2 w-48 animate-pulse"></div>
        </div>
        <div className="card shadow bg-gray-50 dark:bg-gray-800/50 p-6">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-300 dark:bg-gray-600 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            My Prediction Timeline
          </h2>
        </div>
        <div className="card shadow bg-gray-50 dark:bg-gray-800/50 p-6 text-center">
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  const timeSpanLabel = timeSpan === '1month' ? 'Last Month' : timeSpan === '3months' ? 'Last 3 Months' : 'All Season';

  return (
    <div className="max-w-5xl mx-auto mt-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          My Prediction Timeline
        </h2>
      </div>
      
      <div className="card shadow bg-gray-50 dark:bg-gray-800/50" style={{ position: 'relative', overflow: 'visible' }}>
        {/* Header with title and competition selector */}
        <div className="flex flex-col items-stretch border-b border-light-border dark:border-dark-border sm:flex-row">          
          {/* Competition selector buttons */}
          <div className="flex flex-wrap" style={{ overflow: 'hidden' }}>
            {sortedVisibleSeasons.map((season, index) => (
              <button
                key={season.id}
                data-active={effectiveSeasonId === season.id}
                onClick={() => setSelectedSeason(season.id)}
                className={`data-[active=true]:bg-gray-100 dark:data-[active=true]:bg-gray-700/50 relative z-30 flex flex-1 flex-col justify-center gap-1 border-b border-l border-light-border dark:border-dark-border px-4 md:px-4 py-4 text-left sm:border-b-0 sm:border-l sm:min-w-[120px] transition-colors hover:bg-gray-100/50 dark:hover:bg-gray-700/30 ${
                  index === 0 ? 'rounded-tl-[5px]' : ''
                }`}
              >
                <span className="text-xs opacity-70 truncate" style={{ color: 'var(--text-primary)' }}>
                  {season.name}
                </span>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg leading-none font-bold sm:text-xl" style={{ color: 'var(--text-primary)' }}>
                    {seasonStats[season.id]?.points ?? 0}
                  </span>
                  <span className="text-xs opacity-70" style={{ color: 'var(--text-primary)' }}>
                    pts
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Time span selector and legend button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-4 md:px-6 pt-4 pb-3 border-b border-light-border dark:border-dark-border">

           <div className="flex flex-row gap-4">
           <p className="text-sm opacity-70" style={{ color: 'var(--text-primary)' }}>
               <strong>{timeSpanLabel}</strong> • {filteredPredictions.length} predictions, {filteredCorrectCount} correct results
             </p>
           </div>
          
          {/* Time span selector */}
          <div className="flex flex-row gap-2">
          <div className="relative legend-tooltip-container">
            <button
              onClick={() => setShowLegendTooltip(!showLegendTooltip)}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Key</span>
            </button>
            
            {showLegendTooltip && (
              <div 
                className="absolute left-0 top-full mt-2 w-56 p-4 bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-lg shadow-xl"
                style={{ zIndex: 9999 }}
              >
                <h4 className="font-semibold mb-3 text-sm" style={{ color: 'var(--text-primary)' }}>Prediction Status</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#e3bb09' }}></div>
                    <span style={{ color: 'var(--text-primary)' }}>Exact Score</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }}></div>
                    <span style={{ color: 'var(--text-primary)' }}>Correct Result</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }}></div>
                    <span style={{ color: 'var(--text-primary)' }}>Incorrect</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: '#6b7280' }}></div>
                    <span style={{ color: 'var(--text-primary)' }}>Not Yet Scored</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <select
            value={timeSpan}
            onChange={(e) => setTimeSpan(e.target.value as TimeSpan)}
            className="px-3 py-2 text-sm rounded-md border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg focus:ring-2 focus:ring-highlight focus:border-highlight"
            style={{
              color: 'var(--text-primary)'
            }}
          >
            <option value="1month">Last Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="all">All Season</option>
          </select>
          </div>
        </div>

        {/* Chart */}
        <div className="px-2 sm:px-4 pt-4" style={{ position: 'relative', overflow: 'visible', paddingBottom: 0 }}>
          {timelineData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px]">
              <p className="opacity-70" style={{ color: 'var(--text-secondary)' }}>
                No predictions found for the selected period.
              </p>
            </div>
          ) : (
            <div style={{ position: 'relative', overflow: 'visible', width: '100%', height: '300px' }}>
              <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={timelineData}
                margin={{ 
                  left: 12, 
                  right: 12, 
                  top: 5, 
                  bottom: 25 
                }}
                barCategoryGap="10%"
                barSize={undefined}
              >
                <CartesianGrid 
                  vertical={false} 
                  strokeDasharray="3 3" 
                  stroke="var(--border-color)" 
                  opacity={0.2} 
                />
                <YAxis
                  type="number"
                  domain={[0, 'dataMax + 2']}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary)', opacity: 0.8 }}
                  hide
                />
                <XAxis
                  dataKey="label"
                  type="category"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={5}
                  minTickGap={32}
                  tick={{ fontSize: 10, fill: 'var(--text-secondary)', opacity: 0.8}}
                  angle={timelineData.length > 12 ? -45 : 0}
                  textAnchor={timelineData.length > 12 ? 'end' : 'middle'}
                  interval={timelineData.length > 20 ? Math.ceil(timelineData.length / 15) : 0}
                  allowDuplicatedCategory={false}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  wrapperStyle={{ zIndex: 99999, transition: 'none', animation: 'none' }}
                  contentStyle={{ zIndex: 99999, transition: 'none', animation: 'none' }}
                  cursor={false}
                  allowEscapeViewBox={{ x: true, y: true }}
                  animationDuration={0}
                />
                <Bar
                  dataKey="count"
                  shape={<CustomBar />}
                  radius={[0, 0, 0, 0]}
                >
                  {timelineData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="transparent" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictionTimeline;
